import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import uploadToDropbox from '../../services/dropboxService';
import { PageHeader, StatCardRow, Toolbar, ToolbarField, ConfirmDialog } from '../admin';

function GameShow() {
  const [gameShows, setGameShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    startTime: "",
    date: "",
    profileImage: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [questionFormData, setQuestionFormData] = useState({
    questionText: "",
    options: ["", ""],
    correctAnswer: "",
    gameShowId: null,
  });
  const [questions, setQuestions] = useState([]);
  const [editingQuestions, setEditingQuestions] = useState([]);
  const [isEditQuestionModalOpen, setIsEditQuestionModalOpen] = useState(false);
  const [gameShowToDelete, setGameShowToDelete] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch game shows
  useEffect(() => {
    fetchGameShows();
  }, []);

  const fetchGameShows = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/gameshows`
      );
      setGameShows(response.data);
    } catch (error) {
      console.error("Error fetching game shows:", error);
      toast.error("Failed to fetch game shows");
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Update handleImageUpload with loading state
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const downloadUrl = await uploadToDropbox(file);
      setFormData(prev => ({
        ...prev,
        profileImage: downloadUrl
      }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  // Update handleSubmit with loading state
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/gameshows/${editingId}`,
          formData
        );
        toast.success("Game show updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/gameshows`,
          formData
        );
        toast.success("Game show created successfully!");
      }
      setFormData({ name: "", description: "", startTime: "", date: "", profileImage: "" });
      setEditingId(null);
      setIsModalOpen(false);
      fetchGameShows();
    } catch (error) {
      console.error("Error saving game show:", error);
      toast.error(error.response?.data?.message || "Failed to save game show");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete game show
  const handleDelete = async () => {
    if (!gameShowToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/gameshows/${gameShowToDelete.gameShowId}`);
      toast.success("Game show deleted successfully!");
      setGameShows((currentShows) => currentShows.filter((show) => show.gameShowId !== gameShowToDelete.gameShowId));
      setGameShowToDelete(null);
    } catch (error) {
      console.error("Error deleting game show:", error);
      toast.error(error.response?.data?.message || "Failed to delete game show");
    }
  };

  // Update handleEdit
  const handleEdit = (gameShow) => {
    setFormData({
      name: gameShow.name,
      description: gameShow.description,
      startTime: gameShow.startTime,
      date: gameShow.date?.split("T")[0],
      profileImage: gameShow.profileImage || "",
    });
    setEditingId(gameShow.gameShowId);
    setIsModalOpen(true);
  };

  const openModal = () => {
    setFormData({ name: "", description: "", startTime: "", date: "", profileImage: "" });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ name: "", description: "", startTime: "", date: "", profileImage: "" });
    setEditingId(null);
  };

  /* Derived: search-filtered shows */
  const filteredShows = gameShows.filter(
    (g) => !search || g.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Add new handlers for questions
  const handleAddOption = () => {
    setQuestionFormData({
      ...questionFormData,
      options: [...questionFormData.options, ""],
    });
  };

  const handleRemoveOption = (index) => {
    const newOptions = questionFormData.options.filter((_, i) => i !== index);
    setQuestionFormData({
      ...questionFormData,
      options: newOptions,
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...questionFormData.options];
    newOptions[index] = value;
    setQuestionFormData({
      ...questionFormData,
      options: newOptions,
    });
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();

    // Validate the question before adding to local state
    const cleanedOptions = questionFormData.options
      .map((opt) => String(opt).trim())
      .filter((opt) => opt !== "");

    if (cleanedOptions.length < 2) {
      toast.error("At least two options are required");
      return;
    }

    if (!questionFormData.questionText.trim()) {
      toast.error("Question text is required");
      return;
    }

    if (!questionFormData.correctAnswer.trim()) {
      toast.error("Correct answer is required");
      return;
    }

    if (!cleanedOptions.includes(questionFormData.correctAnswer)) {
      toast.error("Correct answer must be one of the options");
      return;
    }

    // Format the single question
    const newQuestion = {
      questionText: questionFormData.questionText.trim(),
      options: cleanedOptions,
      correctAnswer: questionFormData.correctAnswer.trim(),
    };

    // Add to questions collection
    setQuestions([...questions, newQuestion]);

    // Reset form for next question
    setQuestionFormData({
      ...questionFormData,
      questionText: "",
      options: ["", ""], // Start with two empty options
      correctAnswer: "",
    });

    toast.success("Question added! Add another or click Save All Questions.");
  };

  const handleSaveAllQuestions = async () => {
    if (questions.length === 0) {
      toast.error("Please add at least one question");
      return;
    }

    try {
      // Send each question individually to match the database schema
      for (const question of questions) {
        // Ensure all options are strings and there are at least 2 options
        const cleanedOptions = question.options
          .map((opt) => String(opt).trim())
          .filter((opt) => opt !== "");

        if (cleanedOptions.length < 2) {
          toast.error("Each question must have at least two options");
          return;
        }

        const formattedQuestion = {
          gameShowId: questionFormData.gameShowId,
          questionText: question.questionText,
          options: cleanedOptions, // Send as array since model expects DataTypes.JSON
          correctAnswer: String(question.correctAnswer).trim(),
        };

        // Validate before sending
        if (!formattedQuestion.questionText) {
          toast.error("Question text is required");
          return;
        }

        if (!formattedQuestion.correctAnswer) {
          toast.error("Correct answer is required");
          return;
        }

        if (!cleanedOptions.includes(formattedQuestion.correctAnswer)) {
          toast.error("Correct answer must be one of the options");
          return;
        }

        console.log("Sending question:", formattedQuestion); // Debug log

        await axios.post(
          `${process.env.REACT_APP_API_URL}/gameshowquestions`,
          formattedQuestion
        );
      }

      toast.success("All questions saved successfully!");
      setIsQuestionModalOpen(false);
      setQuestions([]);
      setQuestionFormData({
        questionText: "",
        options: ["", ""], // Start with two empty options
        correctAnswer: "",
        gameShowId: null,
      });
    } catch (error) {
      console.error("Error saving questions:", error);
      console.log("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to save questions");
    }
  };

  const openQuestionModal = (gameShowId) => {
    setQuestionFormData({
      ...questionFormData,
      gameShowId: gameShowId,
    });
    setIsQuestionModalOpen(true);
  };

  // Add new function to fetch questions for a game show
  const fetchQuestions = async (gameShowId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/gameshowquestions/${gameShowId}`
      );
      setEditingQuestions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to fetch questions");
      setEditingQuestions([]);
    }
  };

  // Add function to handle opening edit questions modal
  const openEditQuestionModal = async (gameShowId) => {
    await fetchQuestions(gameShowId);
    setQuestionFormData({
      ...questionFormData,
      gameShowId: gameShowId,
    });
    setIsEditQuestionModalOpen(true);
  };

  // Add function to handle updating a question
  const handleUpdateQuestion = async (gameShowQuestionId, question) => {
    try {
      // Format the question data before sending
      const formattedQuestion = {
        gameShowId: questionFormData.gameShowId,
        questionText: question.questionText,
        options: question.options.filter(opt => opt.trim() !== ''), // Remove empty options
        correctAnswer: question.correctAnswer
      };

      // Validate the question data
      if (!formattedQuestion.questionText.trim()) {
        toast.error("Question text is required");
        return;
      }

      if (formattedQuestion.options.length < 2) {
        toast.error("At least two options are required");
        return;
      }

      if (!formattedQuestion.options.includes(formattedQuestion.correctAnswer)) {
        toast.error("Correct answer must be one of the options");
        return;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/gameshowquestions/${gameShowQuestionId}`,
        formattedQuestion
      );
      toast.success("Question updated successfully!");
      await fetchQuestions(questionFormData.gameShowId);
    } catch (error) {
      console.error("Error updating question:", error);
      toast.error("Failed to update question");
    }
  };

  // Add function to handle deleting a question
  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/gameshowquestions/${questionToDelete.gameShowQuestionId}`
      );
      toast.success("Question deleted successfully!");
      setQuestionToDelete(null);
      await fetchQuestions(questionFormData.gameShowId);
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error(error.response?.data?.message || "Failed to delete question");
    }
  };

  return (
    <div className="space-y-4">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {gameShowToDelete && (
        <ConfirmDialog
          title="Delete game show"
          message={`Delete “${gameShowToDelete.name}” and its questions? This action cannot be undone.`}
          confirmLabel="Delete game show"
          onConfirm={handleDelete}
          onCancel={() => setGameShowToDelete(null)}
        />
      )}

      {questionToDelete && (
        <ConfirmDialog
          title="Delete question"
          message={`Delete “${questionToDelete.questionText}”? This action cannot be undone.`}
          confirmLabel="Delete question"
          onConfirm={handleDeleteQuestion}
          onCancel={() => setQuestionToDelete(null)}
        />
      )}

      <PageHeader
        title="Game Shows"
        description="Create shows and manage their questions"
        actions={
          <button type="button" onClick={openModal} className="btn-primary">
            Add new game show
          </button>
        }
      />

      <StatCardRow
        stats={[
          { label: "Total shows", value: gameShows.length },
          {
            label: "Upcoming",
            value: gameShows.filter((g) => g.date && new Date(g.date) >= new Date()).length,
          },
          {
            label: "Past",
            value: gameShows.filter((g) => g.date && new Date(g.date) < new Date()).length,
          },
        ]}
      />

      <Toolbar>
        <ToolbarField label="Search">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by show name…"
            className="field"
          />
        </ToolbarField>
        {search && (
          <button type="button" className="btn-ghost" onClick={() => setSearch('')}>
            Clear
          </button>
        )}
      </Toolbar>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface-800 border border-line-strong rounded-xl w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center px-5 py-4 border-b border-line">
              <h3 className="text-base font-semibold text-white">
                {editingId ? "Edit game show" : "Create new game show"}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-surface-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Game Show Name"
                  className="field"
                  required
                />
              </div>
              <div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description"
                  className="field"
                  rows="3"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  className="field"
                />
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="field"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-white">Game Show Image</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="field"
                    disabled={isUploading}
                  />
                  {isUploading && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>
                {formData.profileImage && (
                  <div className="mt-2">
                    <img 
                      src={formData.profileImage} 
                      alt="Game Show Preview" 
                      className="max-h-40 rounded"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="opacity-0">{editingId ? "Update" : "Create"}</span>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      </div>
                    </>
                  ) : (
                    editingId ? "Update" : "Create"
                  )}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Modified Question Modal */}
      {isQuestionModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface-800 border border-line-strong rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center px-5 py-4 border-b border-line sticky top-0 bg-surface-800 z-10">
              <h3 className="text-base font-semibold text-white">Add questions</h3>
              <button
                type="button"
                onClick={() => setIsQuestionModalOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-surface-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-5">

            {/* Display current number of questions */}
            <div className="mb-4 text-white">
              Questions added: {questions.length}
            </div>

            <form onSubmit={handleQuestionSubmit} className="space-y-4">
              <div>
                <textarea
                  name="questionText"
                  value={questionFormData.questionText}
                  onChange={(e) =>
                    setQuestionFormData({
                      ...questionFormData,
                      questionText: e.target.value,
                    })
                  }
                  placeholder="Question Text"
                  className="field"
                  required
                />
              </div>

              {questionFormData.options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="field"
                    required
                  />
                  {questionFormData.options.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(index)}
                      className="btn-danger !px-3"
                    >
                      -
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddOption}
                className="btn-secondary w-full"
              >
                Add Option
              </button>

              <div>
                <label htmlFor="correctAnswer" className="block text-white mb-2">
                  Correct Answer
                </label>
                <select
                  id="correctAnswer"
                  value={questionFormData.correctAnswer}
                  onChange={(e) =>
                    setQuestionFormData({
                      ...questionFormData,
                      correctAnswer: e.target.value,
                    })
                  }
                  className="field"
                  required
                >
                  <option value="">Select Correct Answer</option>
                  {questionFormData.options.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsQuestionModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-secondary"
                >
                  Add question
                </button>
                <button
                  type="button"
                  onClick={handleSaveAllQuestions}
                  className="btn-primary"
                >
                  Save all questions
                </button>
              </div>
            </form>

            {/* Display added questions */}
            {questions.length > 0 && (
              <div className="mt-4 border-t border-line pt-4">
                <h4 className="eyebrow mb-2">
                  Added questions
                </h4>
                {questions.map((q, idx) => (
                  <div
                    key={idx}
                    className="text-sm mb-2 p-3 bg-surface-900 border border-line rounded-lg"
                  >
                    <p>
                      <strong>Q{idx + 1}:</strong> {q.questionText}
                    </p>
                    <p>
                      <strong>Options:</strong> {q.options.join(", ")}
                    </p>
                    <p>
                      <strong>Correct:</strong> {q.correctAnswer}
                    </p>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Add Edit Questions Modal */}
      {isEditQuestionModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface-800 border border-line-strong rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center px-5 py-4 border-b border-line sticky top-0 bg-surface-800 z-10">
              <h3 className="text-base font-semibold text-white">Edit questions</h3>
              <button
                type="button"
                onClick={() => setIsEditQuestionModalOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-surface-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">

            {editingQuestions.map((question, idx) => (
              <div
                key={question.gameShowQuestionId}
                className="p-4 bg-surface-900 border border-line rounded-lg"
              >
                <h4 className="text-white font-semibold mb-2">
                  Question {idx + 1}
                </h4>
                <textarea
                  value={question.questionText}
                  onChange={(e) => {
                    const updatedQuestions = [...editingQuestions];
                    updatedQuestions[idx] = {
                      ...question,
                      questionText: e.target.value,
                    };
                    setEditingQuestions(updatedQuestions);
                  }}
                  className="field mb-2"
                />
                
                {question.options.map((option, optIdx) => (
                  <input
                    key={optIdx}
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const updatedQuestions = [...editingQuestions];
                      const newOptions = [...question.options];
                      newOptions[optIdx] = e.target.value;
                      updatedQuestions[idx] = {
                        ...question,
                        options: newOptions,
                      };
                      setEditingQuestions(updatedQuestions);
                    }}
                    className="field mb-2"
                  />
                ))}

                <label htmlFor={`correctAnswer-${idx}`} className="block text-white mb-2">
                  Correct Answer
                </label>
                <select
                  id={`correctAnswer-${idx}`}
                  value={question.correctAnswer}
                  onChange={(e) => {
                    const updatedQuestions = [...editingQuestions];
                    updatedQuestions[idx] = {
                      ...question,
                      correctAnswer: e.target.value,
                    };
                    setEditingQuestions(updatedQuestions);
                  }}
                  className="field mb-2"
                >
                  {question.options.map((option, optIdx) => (
                    <option key={optIdx} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => handleUpdateQuestion(question.gameShowQuestionId, question)}
                    className="btn-secondary !py-1.5 !px-3"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuestionToDelete(question)}
                    className="btn-danger !py-1.5 !px-3"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-14 flex flex-col items-center text-gray-400">
            <div className="w-8 h-8 border-2 border-line-strong border-t-accent rounded-full animate-spin" />
            <p className="mt-4 text-sm">Loading game shows…</p>
          </div>
        ) : filteredShows.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-semibold text-white">
              {search ? 'No shows match your search' : 'No game shows yet'}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {search ? 'Try a different name.' : 'Create your first show to get started.'}
            </p>
            {!search && (
              <button type="button" onClick={openModal} className="btn-primary mt-5">
                Add new game show
              </button>
            )}
          </div>
        ) : (
        <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="table-head">
              <th className="px-3.5 py-2.5 text-left">Image</th>
              <th className="px-3.5 py-2.5 text-left">Name</th>
              <th className="px-3.5 py-2.5 text-left">Description</th>
              <th className="px-3.5 py-2.5 text-left">Start time</th>
              <th className="px-3.5 py-2.5 text-left">Date</th>
              <th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 z-10 border-l border-line-strong">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredShows.map((gameShow) => (
              <tr key={gameShow.gameShowId} className="table-row group">
                <td className="px-3.5 py-2.5">
                  {gameShow.profileImage ? (
                    <img 
                      src={gameShow.profileImage} 
                      alt={gameShow.name}
                      className="w-14 h-14 object-cover rounded-lg border border-line-strong"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-surface-700 border border-line-strong rounded-lg flex items-center justify-center text-[10px] text-gray-500">
                      No image
                    </div>
                  )}
                </td>
                <td className="px-3.5 py-2.5 text-white">{gameShow.name}</td>
                <td className="px-3.5 py-2.5 text-gray-400 max-w-xs truncate">{gameShow.description}</td>
                <td className="px-3.5 py-2.5 text-gray-300 tabular-nums">{gameShow.startTime}</td>
                <td className="px-3.5 py-2.5 text-gray-300 tabular-nums">
                  {new Date(gameShow.date).toLocaleDateString()}
                </td>
                <td className="p-2 sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-l border-line transition-colors text-right">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleEdit(gameShow)}
                    className="btn-ghost !py-1.5 !px-3"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setGameShowToDelete(gameShow)}
                    className="btn-danger !py-1.5 !px-3"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => openQuestionModal(gameShow.gameShowId)}
                    className="btn-secondary !py-1.5 !px-3"
                  >
                    Add Questions
                  </button>
                  <button
                    onClick={() => openEditQuestionModal(gameShow.gameShowId)}
                    className="btn-ghost !py-1.5 !px-3"
                  >
                    Edit Questions
                  </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
      </div>
    </div>
  );
}

export default GameShow;
